import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type PositionStrategy = 'fixed' | 'below' | 'above' | 'auto'

type PortalDropdownProps = {
  isOpen: boolean
  children: ReactNode
  position?: { top: number; left: number }
  anchorRef?: React.RefObject<HTMLElement | null>
  dropdownRef?: React.RefObject<HTMLDivElement | null>
  portalContainer?: Element | null
  strategy?: PositionStrategy
  offset?: number
  className?: string
  onClose?: () => void
}

export function PortalDropdown({
  isOpen,
  children,
  position,
  anchorRef,
  dropdownRef,
  portalContainer,
  strategy = 'auto',
  offset = 8,
  className = '',
  onClose,
}: PortalDropdownProps) {
  const [viewportSize, setViewportSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  })

  // Track viewport size changes
  useEffect(() => {
    if (!isOpen) return

    const handleResize = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  // Close on scroll if onClose is provided
  useEffect(() => {
    if (!isOpen || !onClose) return

    const handleScroll = () => {
      onClose()
    }

    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [isOpen, onClose])

  // Calculate adjusted position
  const adjustedPosition = useMemo(() => {
    if (!isOpen) return { top: 0, left: 0 }

    // Use provided position if available
    if (position) {
      if (!dropdownRef?.current) return position

      const menuRect = dropdownRef.current.getBoundingClientRect()
      let newTop = position.top
      let newLeft = position.left

      // Adjust for viewport boundaries
      if (position.left + menuRect.width > viewportSize.width) {
        newLeft = viewportSize.width - menuRect.width - offset
      }
      if (position.top + menuRect.height > viewportSize.height) {
        newTop = viewportSize.height - menuRect.height - offset
      }
      if (newLeft < offset) newLeft = offset
      if (newTop < offset) newTop = offset

      return { top: newTop, left: newLeft }
    }

    // Calculate position from anchor if provided
    if (anchorRef?.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect()
      const menuRect = dropdownRef?.current?.getBoundingClientRect()
      const menuHeight = menuRect?.height || 350
      const menuWidth = menuRect?.width || 200

      let newTop = anchorRect.top
      let newLeft = anchorRect.left

      if (strategy === 'below') {
        newTop = anchorRect.bottom + offset
      } else if (strategy === 'above') {
        newTop = anchorRect.top - menuHeight - offset
      } else if (strategy === 'auto') {
        // Try above first
        newTop = anchorRect.top - menuHeight - offset
        // If goes off top, position below
        if (newTop < offset) {
          newTop = anchorRect.bottom + offset
        }
      }

      // Adjust for viewport boundaries
      if (newLeft + menuWidth > viewportSize.width) {
        newLeft = viewportSize.width - menuWidth - offset
      }
      if (newLeft < offset) {
        newLeft = offset
      }

      return { top: newTop, left: newLeft }
    }

    return { top: 0, left: 0 }
  }, [
    isOpen,
    position?.top,
    position?.left,
    anchorRef,
    dropdownRef,
    viewportSize.width,
    viewportSize.height,
    strategy,
    offset,
  ])

  if (!isOpen || typeof document === 'undefined') {
    return null
  }

  const target = portalContainer || document.body

  return createPortal(
    <div
      ref={dropdownRef}
      className={`font-hanken-grotesk fixed z-[9999] ${className}`}
      style={{
        top: `${adjustedPosition.top}px`,
        left: `${adjustedPosition.left}px`,
      }}
    >
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>,
    target
  )
}
